import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { pb } from '@/core/api';

import type { UserSetting } from '@/types/pocketbase';

const QUERY_KEY = ['user-settings'] as const;

export function useUserSettings(): UseQueryResult<UserSetting[]> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<UserSetting[]> => {
      return pb.collection('user_settings').getFullList<UserSetting>();
    },
  });
}

export function useUserSetting(
  key: string
): UseQueryResult<UserSetting | null> {
  return useQuery({
    queryKey: [...QUERY_KEY, key],
    queryFn: async (): Promise<UserSetting | null> => {
      try {
        return await pb
          .collection('user_settings')
          .getFirstListItem<UserSetting>(`key = "${key}"`);
      } catch {
        return null;
      }
    },
  });
}

type MonitoringResult = UseQueryResult<UserSetting | null> & {
  isActive: boolean;
};

export function useMonitoringActive(): MonitoringResult {
  const result = useUserSetting('monitoring_active');
  return { ...result, isActive: result.data?.value === 'true' };
}

type UpdateInput = { id: string; value: string };
type UpdateResult = UseMutationResult<UserSetting, Error, UpdateInput>;

export function useUpdateUserSetting(): UpdateResult {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, value }: UpdateInput) => {
      return pb.collection('user_settings').update<UserSetting>(id, { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
